import { useCallback, useEffect, useState } from 'react';
import { viduFeedList } from '@/app/apis';
import { useToast } from '@/app/components/ui/use-toast';
import type { ViduV1InspirationItem } from '@/app/types';
import { useFetch } from './use-fetch';

const PAGE_SIZE = 20;

export const useFeedList = () => {
  const { toast } = useToast();
  const [feedList, setFeedList] = useState<(ViduV1InspirationItem & { traceId?: string })[]>([]);
  const [nextPageToken, setNextPageToken] = useState<string>('');
  const [hasMore, setHasMore] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const { run: fetchFeedList, loading: feedListLoading } = useFetch(
    (pageToken?: string) => {
      return viduFeedList({
        query: {
          'pager.page_token': pageToken,
          'pager.pagesz': PAGE_SIZE,
          channel_type: 'mixed',
          sort_type: 'recommended',
        },
      });
    },
    {
      manual: true,
      onSuccess: ({ data, error, response }, params) => {
        if (error) {
          toast({
            type: 'warning',
            message: error.message,
          });
          setIsInitialLoading(false);
          return;
        }
        if (data) {
          const newList = data.list || [];
          const traceId = response.headers.get('x-md-trace-id') || '';
          const listWithTraceId = newList.map((item) => ({
            ...item,
            traceId,
          }));

          const isFirstPage = !params[0];
          setFeedList((prev) => (isFirstPage ? listWithTraceId : [...prev, ...listWithTraceId]));
          setNextPageToken(data.next_page_token || '');
          setHasMore(!!data.next_page_token);
          setIsInitialLoading(false);
        }
      },
    },
  );

  // 初始化时获取列表数据
  useEffect(() => {
    fetchFeedList();
  }, []);

  // 处理加载更多
  const handleLoadMore = useCallback(() => {
    if (!feedListLoading && hasMore && nextPageToken) {
      fetchFeedList(nextPageToken);
    }
  }, [feedListLoading, hasMore, nextPageToken, fetchFeedList]);

  return {
    feedList,
    hasMore,
    isInitialLoading,
    isLoading: feedListLoading,
    handleLoadMore,
  };
};
